import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { Circuit } from '@multirx/shared/types/track.js';
import {
  buildJokerLap3dTiles,
  buildStartLane3dTiles,
  buildTrack3dTiles,
  type Track3dTile,
} from './track3dLayout.js';
import {
  TILE_SIZE,
  anchorWithOffset,
  centerOffset,
  computeTrackSceneBounds,
  computeVehicleModelScale,
  loadFreeTrackSceneDecor,
  loadModel,
  loadTrackSceneDecor,
  loadTrackSceneGrass,
  loadTrackSceneRoad,
  setupTrackSceneAtmosphere,
} from './circuit3dTrackScene.js';
import { buildProceduralCorner } from './proceduralRoadPieces.js';

/**
 * Scène 3D de l'éditeur de circuit — rendu du tracé (mêmes assets/textures que
 * l'aperçu 3D via `circuit3dTrackScene.ts`), caméra contrainte pour un placement
 * précis, ciblage de case par raycasting sur un plan de sol (indépendant de
 * l'angle/zoom de la caméra), et clic sur une case pour piloter le placement/
 * retrait de pièce (logique de placement elle-même déléguée à l'appelant via
 * `onCellClick` — cette scène ne connaît aucune règle métier).
 */

export interface GridCell {
  x: number;
  y: number;
}

export interface HighlightCell extends GridCell {
  /** Couleur hex (0xRRGGBB) ; `HOVER_HIGHLIGHT_COLOR` par défaut si omise. */
  color?: number;
}

export interface GhostCornerPreview {
  /** Case du virage lui-même (pivot), pas le bras — même convention que `createTileGroup`. */
  pivot: GridCell;
  /** Pas de 90° de la pièce (0 à 3) — même convention que `Track3dTile.rotationSteps`. */
  rotationSteps: number;
  armCells: 1 | 2 | 3;
  color: number;
}

/** Point d'intersection rayon/sol brut (unités de case, PAS arrondi à une case entière). */
export interface GroundPoint {
  x: number;
  y: number;
}

/** Aperçu fantôme d'un modèle catalogue (pièce route/décor) — voir `setGhostModel`. */
export interface GhostModelPreview {
  /** URL publique du `.glb` (`CircuitPiece3dCatalogEntry.publicPath`). */
  url: string;
  x: number;
  y: number;
  /** Rotation libre en degrés (pas seulement des paliers de 90°). */
  rotationDeg: number;
  /** `CircuitPiece3dCatalogEntry.carModel` — voir `computeVehicleModelScale`. */
  carModel?: boolean;
}

export interface Circuit3dEditorSceneOptions {
  /** Appelé à chaque case survolée par le curseur (null si hors grille). */
  onHoverCell?(cell: GridCell | null): void;
  /**
   * Appelé à CHAQUE déplacement du curseur (jamais throttlé par case, contrairement
   * à `onHoverCell`) — dédié au suivi fluide de l'aperçu fantôme d'un décor en
   * placement libre (voir `setGhostModel`), qui doit suivre le curseur en continu et
   * pas seulement case par case. `null` hors grille.
   */
  onHoverPoint?(point: GroundPoint | null): void;
  /**
   * Appelé sur un clic net (pas un glisser de caméra) sur une case de la grille.
   * `point` est la position flottante brute du raycast (avant arrondi) — utilisé
   * par le placement libre de décor (voir `circuit3dEditor.ts`), ignoré par les
   * modes qui ne travaillent qu'en cases entières (Tracer, pièces de route).
   */
  onCellClick?(cell: GridCell, point: GroundPoint): void;
  /**
   * Appelé sur un clic droit net (pas un glisser-orbiter) sur une case de la
   * grille — le menu contextuel natif du navigateur est systématiquement
   * supprimé (voir le listener `contextmenu`). Le sens de ce clic droit dépend
   * entièrement de l'appelant (annuler la dernière case pendant le tracé,
   * supprimer une pièce/un décor une fois le tracé fini — voir
   * `circuit3dEditor.ts`) ; cette scène ne fait que transmettre l'événement.
   */
  onCellRightClick?(cell: GridCell, point: GroundPoint): void;
  /**
   * Appelé en continu tant que le bouton molette (du milieu) est maintenu et que
   * la souris se déplace — remplace l'ancien bouton "Tourner décor" : l'appelant
   * décide quoi faire tourner (décor déjà sélectionné, ou rotation en attente
   * avant placement, voir `circuit3dEditor.ts`). `deltaDeg` est l'incrément
   * depuis le dernier événement (pas un angle absolu), positif ou négatif selon
   * le sens du glisser horizontal. Le bouton molette de la caméra (zoom/dolly
   * par défaut d'OrbitControls) est désactivé dans cette scène : la molette
   * (scroll) reste le seul moyen de zoomer.
   */
  onMiddleDragRotate?(deltaDeg: number): void;
  /**
   * Appelé en continu tant que Maj + clic gauche est maintenu et que la souris se
   * déplace verticalement — déplace le décor sélectionné sur l'axe Y (monter/descendre)
   * au lieu de le sélectionner/déplacer au sol (voir `circuit3dEditor.ts`). `deltaElevation`
   * est l'incrément depuis le dernier événement (mêmes unités que `TILE_SIZE`, pas une
   * élévation absolue), positif = monter (glisser vers le haut de l'écran). L'orbite
   * caméra (qui utilise aussi le bouton gauche) est désactivée le temps du geste pour
   * ne pas tourner la vue en même temps qu'on monte/descend le décor.
   */
  onShiftDragElevate?(deltaElevation: number): void;
}

export interface Circuit3dEditorSceneHandle {
  dispose(): void;
  /** Bascule entre vue orbitale libre et vue du dessus (précision de placement). */
  setTopView(enabled: boolean): void;
  isTopView(): boolean;
  /** Recadre la caméra sur le tracé (récupération après une orbite désorientée). */
  recenter(): void;
  /** Reconstruit les pièces de route et le décor (après une mutation du circuit), sans toucher à la caméra. */
  refreshScene(circuit: Circuit): void;
  /**
   * Surligne un ensemble de cases (aperçu d'empreinte avant placement — 1 case pour
   * une pièce simple, N cases alignées pour une pièce multi-case) ; tableau vide
   * pour tout masquer. Cette scène ne calcule pas l'empreinte elle-même (aucune
   * règle métier ici) — l'appelant fournit la liste déjà résolue.
   */
  setHighlightCells(cells: HighlightCell[]): void;
  /**
   * Aperçu fantôme 3D (semi-transparent) du virage qui serait posé sur la case
   * actuellement survolée — géométrie réelle (`buildProceduralCorner`), pas une
   * simple case colorée : montre la courbe et son orientation exactes avant de
   * cliquer. `null` pour le masquer (hors survol d'une case de virage).
   */
  setGhostCorner(preview: GhostCornerPreview | null): void;
  /**
   * Aperçu fantôme 3D (semi-transparent) d'une pièce catalogue (route ou décor,
   * modèle `.glb` chargé — mis en cache par URL, jamais rechargé en resurvolant la
   * même pièce) à la position/rotation données. `null` pour le masquer.
   */
  setGhostModel(preview: GhostModelPreview | null): void;
  /**
   * Surligne en rouge les cases en erreur de validation (chevauchement, virage
   * trop court, tracé ouvert — voir `getCircuitEditErrorCells`) ; tableau vide
   * pour tout masquer. Persistant (contrairement à `setHighlightCells`, qui est
   * un aperçu transitoire au survol) — l'appelant le met à jour après chaque
   * mutation du circuit.
   */
  setErrorCells(cells: GridCell[]): void;
  /**
   * Libère volontairement le contexte WebGL le temps qu'une modale avec ses
   * propres aperçus 3D (catalogue de pièces) reste ouverte par-dessus — sinon,
   * le total de contextes actifs peut dépasser la limite du navigateur et lui
   * faire perdre le contexte de CETTE scène (canvas qui devient blanc). Three.js
   * réattribue automatiquement les textures/géométries à la restauration.
   */
  pauseForOverlay(): void;
  resumeFromOverlay(): void;
  /**
   * Repères de coins (rouge = case au style par défaut, violet = case couverte
   * par une pièce groupée) posés aux 4 coins de chaque case, purement calculés
   * depuis les coordonnées de grille — donc TOUJOURS positionnés correctement,
   * indépendamment du modèle 3D affiché. Comparer visuellement un repère à la
   * surface de route réellement visible permet de voir tout décalage/trou du
   * modèle sans deviner sur une capture d'écran (technique proposée en session
   * de debug pour vérifier les virages Large/Larger groupés).
   */
  setDebugCornerMarkers(enabled: boolean): void;
}

// Jaune franc plutôt qu'un vert-teal proche de la couleur de l'herbe (0x73eddd) —
// les deux étaient trop proches en teinte pour bien ressortir en vue du dessus.
export const HOVER_HIGHLIGHT_COLOR = 0xffd23f;
export const ERROR_HIGHLIGHT_COLOR = 0xe0554f;
/** Distance de glisser (px) au-delà de laquelle un pointerdown→up est traité comme une orbite, pas un clic. */
const CLICK_DRAG_TOLERANCE_PX = 4;

function disposeGroupChildren(group: THREE.Group): void {
  for (const obj of [...group.children]) {
    obj.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        const material = child.material;
        for (const m of Array.isArray(material) ? material : [material]) {
          if (m instanceof THREE.MeshStandardMaterial) m.map?.dispose();
          m.dispose();
        }
      }
    });
    group.remove(obj);
  }
}

function buildAllTiles(circuit: Circuit): Track3dTile[] {
  return [
    ...buildTrack3dTiles(circuit),
    ...buildJokerLap3dTiles(circuit),
    ...buildStartLane3dTiles(circuit),
  ];
}

export function mountCircuit3dEditorScene(
  container: HTMLElement,
  circuit: Circuit,
  options?: Circuit3dEditorSceneOptions
): Circuit3dEditorSceneHandle {
  const width = container.clientWidth || 480;
  const height = container.clientHeight || 360;
  /** Circuit le plus récent connu — sert à reconstruire les repères de debug à
   * la demande (`setDebugCornerMarkers`) sans dépendre du dernier `refreshScene`. */
  let latestCircuit = circuit;

  const scene = new THREE.Scene();
  (window as unknown as { __debugScene?: THREE.Scene }).__debugScene = scene;

  let gridWidth = circuit.width;
  let gridHeight = circuit.height;
  const allTiles = buildAllTiles(circuit);
  const bounds = computeTrackSceneBounds(allTiles, {
    width: circuit.width,
    height: circuit.height,
  });
  const { centerX, centerZ, span } = bounds;

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 500);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  (
    window as unknown as { __debugRC?: { camera: THREE.Camera; renderer: THREE.WebGLRenderer } }
  ).__debugRC = { camera, renderer };
  renderer.domElement.className = 'circuit-3d-editor-canvas';
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(centerX, 0, centerZ);
  // Contrainte plus stricte que l'aperçu en lecture seule (qui ne borne que le haut) :
  // un minPolarAngle évite le passage en vue de dessus désorientante via l'orbite libre
  // (on y accède explicitement via `setTopView`) et un maxPolarAngle évite l'horizon
  // rasant où les cases se chevauchent visuellement.
  controls.minPolarAngle = Math.PI * 0.15;
  controls.maxPolarAngle = Math.PI * 0.49;
  // Assez proche pour distinguer le détail d'une pièce/décor à l'écran (plancher absolu
  // pour les petits circuits, sinon `span * 0.3` gardait la caméra bien trop loin dès
  // qu'on voulait zoomer sur une case précise).
  controls.minDistance = Math.max(1.2, span * 0.08);
  controls.maxDistance = span * 3;
  // Le bouton molette (du milieu) sert désormais à faire pivoter un décor
  // (voir `onMiddleDragRotate`) plutôt qu'à zoomer/dolly la caméra — le zoom
  // à la molette (scroll) reste intact, indépendant de `mouseButtons`.
  controls.mouseButtons.MIDDLE = null;
  // Glisser du clic droit = pan natif d'`OrbitControls` par défaut — mais son axe
  // vertical (haut/bas de la souris) déplace la caméra le long de son axe local
  // "haut", qui bascule fortement une fois la vue inclinée et l'envoie facilement
  // dans le ciel ou sous le sol (repéré en testant). Désactivé au profit d'un pan
  // manuel gauche/droite uniquement (voir `panCameraHorizontal` plus bas, et
  // `handlePointerMove`/`rightPointerDownAt`), qui ignore totalement le glisser
  // vertical — le clic droit court (sans glisser) reste par ailleurs la
  // suppression sous le curseur (`onCellRightClick`), inchangée.
  controls.mouseButtons.RIGHT = null;

  let disposed = false;
  let topView = false;

  // `refreshScene` peut être appelée plusieurs fois en rafale (un clic de
  // tracé = un appel) avant que le chargement asynchrone précédent (fetch du
  // catalogue + modèles réseau) n'ait fini de peupler le groupe — sans garde,
  // un appel PLUS ANCIEN mais qui se résout PLUS TARD (ex. re-télécharge des
  // modèles par défaut non mis en cache) écrase le rendu correct d'un appel
  // plus récent : une pièce groupée fraîchement posée (ex. virage "Très large")
  // se retrouve remplacée par le rendu par défaut, sans aucune erreur visible.
  // Chaque appel capture son propre numéro de génération ; seul le plus récent
  // est autorisé à peupler le groupe une fois son chargement terminé.
  let roadGeneration = 0;
  let decorGeneration = 0;

  setupTrackSceneAtmosphere(scene, bounds);
  loadTrackSceneGrass(scene, bounds, () => disposed);

  const roadGroup = new THREE.Group();
  scene.add(roadGroup);
  {
    const myGeneration = ++roadGeneration;
    void loadTrackSceneRoad(roadGroup, allTiles, () => disposed || myGeneration !== roadGeneration);
  }

  const decorGroup = new THREE.Group();
  scene.add(decorGroup);
  {
    const myGeneration = ++decorGeneration;
    const isDisposedNow = () => disposed || myGeneration !== decorGeneration;
    void loadTrackSceneDecor(decorGroup, circuit, isDisposedNow);
    void loadFreeTrackSceneDecor(decorGroup, circuit, isDisposedNow);
  }

  // Repères de coins de debug (voir `setDebugCornerMarkers` sur le handle) —
  // désactivés par défaut, reconstruits à chaque `refreshScene` s'ils sont actifs.
  let debugMarkersEnabled = false;
  const debugMarkerGroup = new THREE.Group();
  scene.add(debugMarkerGroup);
  const debugMarkerGeometry = new THREE.SphereGeometry(0.08, 12, 12);
  const debugMarkerDefaultMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
  const debugMarkerGroupedMaterial = new THREE.MeshBasicMaterial({ color: 0x8800ff });
  function rebuildDebugMarkers(forCircuit: Circuit): void {
    debugMarkerGroup.clear();
    if (!debugMarkersEnabled) return;
    for (const tile of buildAllTiles(forCircuit)) {
      const material =
        tile.pieceSlug || tile.pieceCovered
          ? debugMarkerGroupedMaterial
          : debugMarkerDefaultMaterial;
      const corners: Array<[number, number]> = [
        [tile.x, tile.y],
        [tile.x + 1, tile.y],
        [tile.x, tile.y + 1],
        [tile.x + 1, tile.y + 1],
      ];
      for (const [cx, cz] of corners) {
        const sphere = new THREE.Mesh(debugMarkerGeometry, material);
        sphere.position.set(cx * TILE_SIZE, 0.1, cz * TILE_SIZE);
        debugMarkerGroup.add(sphere);
      }
    }
  }
  rebuildDebugMarkers(circuit);

  const perspectivePosition = new THREE.Vector3(
    centerX + span * 0.7,
    span * 0.85,
    centerZ + span * 0.9
  );
  const sceneFog = scene.fog;
  function framePerspective(): void {
    scene.fog = sceneFog;
    camera.position.copy(perspectivePosition);
    controls.target.set(centerX, 0, centerZ);
    controls.enableRotate = true;
    controls.minPolarAngle = Math.PI * 0.15;
    controls.maxPolarAngle = Math.PI * 0.49;
    controls.update();
  }
  function frameTopView(): void {
    // Le brouillard (pensé pour l'horizon en vue orbitale) rendrait la vue du
    // dessus quasi invisible à cette hauteur de caméra : désactivé ici, restauré
    // au retour en vue orbitale.
    scene.fog = null;
    camera.position.set(centerX, span * 2.2, centerZ + 0.001);
    controls.target.set(centerX, 0, centerZ);
    // Orbite désactivée en vue du dessus : seuls pan/zoom restent utiles pour un
    // placement précis façon éditeur 2D ("mode plan").
    controls.enableRotate = false;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = 0;
    controls.update();
  }
  framePerspective();

  // Case survolée : calculée par intersection du rayon caméra/souris avec le plan
  // y=0 (pur calcul géométrique, indépendant de l'angle/zoom de la caméra) plutôt
  // que par raycasting contre les meshes de route — fiable même avant leur chargement.
  const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const raycaster = new THREE.Raycaster();
  const pointerNdc = new THREE.Vector2();
  const hitPoint = new THREE.Vector3();
  let hoveredCell: GridCell | null = null;

  // Surlignage (aperçu d'empreinte au survol, ou cases plaçables en mode Tracer) :
  // groupe reconstruit à chaque appel — le nombre de cases varie (1 à 3 pour une
  // pièce multi-case, jusqu'à 5 pour les cases plaçables en mode Tracer).
  const highlightGeometry = new THREE.PlaneGeometry(TILE_SIZE * 0.96, TILE_SIZE * 0.96);
  // Un matériau par couleur distincte (mis en cache, jamais disposé — palette
  // fixe et petite : vert/jaune/rouge des tailles de virage + doré "fermer la
  // boucle") plutôt qu'un seul matériau partagé, pour surligner plusieurs
  // cases de couleurs différentes en même temps (voir `HighlightCell.color`).
  const highlightMaterials = new Map<number, THREE.MeshBasicMaterial>();
  function highlightMaterialFor(color: number): THREE.MeshBasicMaterial {
    let material = highlightMaterials.get(color);
    if (!material) {
      material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
      });
      highlightMaterials.set(color, material);
    }
    return material;
  }
  const highlightGroup = new THREE.Group();
  scene.add(highlightGroup);

  function applyHighlightCells(cells: HighlightCell[]): void {
    highlightGroup.clear();
    for (const cell of cells) {
      const mesh = new THREE.Mesh(
        highlightGeometry,
        highlightMaterialFor(cell.color ?? HOVER_HIGHLIGHT_COLOR)
      );
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set((cell.x + 0.5) * TILE_SIZE, 0.01, (cell.y + 0.5) * TILE_SIZE);
      highlightGroup.add(mesh);
    }
  }

  // Aperçu fantôme du virage survolé : géométrie réelle (même fonction que le
  // rendu final, voir `circuit3dTrackScene.ts`), recolorée en semi-transparent
  // plutôt que reconstruite à chaque fois (bon marché, aucun chargement réseau).
  const ghostGroup = new THREE.Group();
  scene.add(ghostGroup);
  const ghostMaterials = new Map<number, THREE.MeshBasicMaterial>();
  function ghostMaterialFor(color: number): THREE.MeshBasicMaterial {
    let material = ghostMaterials.get(color);
    if (!material) {
      material = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      });
      ghostMaterials.set(color, material);
    }
    return material;
  }
  function applyGhostCorner(preview: GhostCornerPreview | null): void {
    // Ne dispose QUE la géométrie (fraîche à chaque appel, voir `buildProceduralCorner`) —
    // pas le matériau, partagé/mis en cache (voir `ghostMaterialFor`) : le disposer casserait
    // sa réutilisation pour le prochain survol (`disposeGroupChildren` disposerait les deux).
    for (const obj of [...ghostGroup.children]) {
      obj.traverse((child) => {
        if (child instanceof THREE.Mesh) child.geometry.dispose();
      });
      ghostGroup.remove(obj);
    }
    if (!preview) return;
    const model = buildProceduralCorner(preview.armCells);
    const material = ghostMaterialFor(preview.color);
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) child.material = material;
    });
    model.position.set(
      (preview.pivot.x + 0.5) * TILE_SIZE,
      0.02,
      (preview.pivot.y + 0.5) * TILE_SIZE
    );
    model.rotation.y = -(preview.rotationSteps * Math.PI) / 2;
    ghostGroup.add(model);
  }

  // Aperçu fantôme d'un modèle catalogue (route/décor) au survol en mode
  // Personnaliser : contrairement au virage (géométrie procédurale synchrone),
  // c'est un .glb chargé par réseau — mis en cache par URL une fois chargé (jamais
  // rechargé en resurvolant la même pièce), avec un jeton de requête pour ignorer
  // une réponse devenue obsolète si l'aperçu change avant la fin du chargement.
  const GHOST_MODEL_COLOR = 0xffffff;
  const ghostModelGroup = new THREE.Group();
  scene.add(ghostModelGroup);
  const ghostModelTemplates = new Map<string, THREE.Object3D>();
  let ghostModelRequestToken = 0;

  function clearGhostModel(): void {
    for (const obj of [...ghostModelGroup.children]) {
      obj.traverse((child) => {
        if (child instanceof THREE.Mesh) child.geometry.dispose();
      });
      ghostModelGroup.remove(obj);
    }
  }

  function placeGhostModel(template: THREE.Object3D, preview: GhostModelPreview): void {
    clearGhostModel();
    const material = ghostMaterialFor(GHOST_MODEL_COLOR);
    const instance = template.clone(true);
    instance.traverse((child) => {
      if (child instanceof THREE.Mesh) child.material = material;
    });
    instance.position.set(preview.x * TILE_SIZE, 0.02, preview.y * TILE_SIZE);
    instance.rotation.y = -(preview.rotationDeg * Math.PI) / 180;
    ghostModelGroup.add(instance);
  }

  function applyGhostModel(preview: GhostModelPreview | null): void {
    if (!preview) {
      clearGhostModel();
      return;
    }
    const cached = ghostModelTemplates.get(preview.url);
    if (cached) {
      placeGhostModel(cached, preview);
      return;
    }
    const token = ++ghostModelRequestToken;
    clearGhostModel();
    void loadModel(preview.url)
      .then((model) => {
        // Même échelle que le rendu réel (voir `loadFreeTrackSceneDecor`) pour un
        // modèle voiture — sinon le fantôme affiché avant placement ne correspond
        // pas à la taille du décor une fois posé.
        if (preview.carModel) model.scale.setScalar(computeVehicleModelScale(model));
        // Recentre comme le rendu réel (`loadFreeTrackSceneDecor`/`loadTrackSceneDecor`) :
        // le modèle Kenney brut garde un pivot arbitraire hérité du kit d'origine, sans
        // ce recentrage le fantôme apparaîtrait décalé par rapport à la pose finale.
        const centered = anchorWithOffset(model, centerOffset(model));
        // Toujours mettre en cache, même si un survol plus récent a déjà pris le
        // relais — seul l'AFFICHAGE de cette réponse-ci devient inutile.
        ghostModelTemplates.set(preview.url, centered);
        if (token !== ghostModelRequestToken) return;
        placeGhostModel(centered, preview);
      })
      .catch(() => {
        /* Pas d'aperçu si le modèle échoue à charger — non bloquant. */
      });
  }

  // Cases en erreur de validation : groupe reconstruit à chaque appel (le nombre
  // de cases en erreur varie librement, contrairement au pool fixe du survol).
  const errorGroup = new THREE.Group();
  scene.add(errorGroup);
  const errorMaterial = new THREE.MeshBasicMaterial({
    color: ERROR_HIGHLIGHT_COLOR,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
  });
  function applyErrorCells(cells: GridCell[]): void {
    // `.clear()`, pas `disposeGroupChildren` : ces meshes partagent la géométrie du
    // survol et un matériau commun à toutes les cases en erreur (voir plus bas),
    // les disposer à chaque reconstruction casserait ces ressources partagées.
    errorGroup.clear();
    for (const cell of cells) {
      const mesh = new THREE.Mesh(highlightGeometry, errorMaterial);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set((cell.x + 0.5) * TILE_SIZE, 0.015, (cell.y + 0.5) * TILE_SIZE);
      errorGroup.add(mesh);
    }
  }

  function setHoveredCell(cell: GridCell | null): void {
    const changed =
      (cell?.x ?? null) !== (hoveredCell?.x ?? null) ||
      (cell?.y ?? null) !== (hoveredCell?.y ?? null);
    hoveredCell = cell;
    if (changed) {
      // Le surlignage au survol est entièrement délégué à l'appelant (via
      // `onHoverCell` + `setHighlightCells`) : cette scène ne décide plus elle-même
      // d'afficher la case survolée. Un appel automatique ici écraserait le
      // surlignage persistant des cases plaçables du mode Tracer dès le moindre
      // mouvement de souris — l'appelant sait, lui, s'il doit remplacer ce
      // surlignage (mode Personnaliser, aperçu d'empreinte) ou le laisser tel quel
      // (mode Tracer, où seul un clic doit le faire évoluer).
      options?.onHoverCell?.(cell);
    }
  }

  /** Intersection rayon/sol brute (unités de case), sans arrondi. */
  function pointerToGroundPoint(e: PointerEvent): GroundPoint | null {
    const rect = renderer.domElement.getBoundingClientRect();
    pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointerNdc, camera);
    if (!raycaster.ray.intersectPlane(groundPlane, hitPoint)) return null;
    return { x: hitPoint.x / TILE_SIZE, y: hitPoint.z / TILE_SIZE };
  }

  function gridCellFromPoint(point: GroundPoint | null): GridCell | null {
    if (!point) return null;
    const gx = Math.floor(point.x);
    const gy = Math.floor(point.y);
    if (gx < 0 || gy < 0 || gx >= gridWidth || gy >= gridHeight) return null;
    return { x: gx, y: gy };
  }

  // Rotation de décor au clic molette + glisser (voir `onMiddleDragRotate`) —
  // `lastMiddleDragAt` (pas la position de départ du geste, contrairement aux
  // trackers clic gauche/droit ci-dessous) : le delta se calcule d'un événement
  // au suivant pour une rotation fluide et continue, pas d'un seuil clic/glisser
  // à distinguer (le bouton molette ne déclenche jamais de clic ici).
  const MIDDLE_DRAG_ROTATE_DEG_PER_PX = 0.5;
  let lastMiddleDragAt: { x: number; y: number } | null = null;

  // Élévation de décor au Maj + clic gauche + glisser (voir `onShiftDragElevate`) —
  // même principe que `lastMiddleDragAt` (delta d'un événement au suivant), mais sur
  // le bouton gauche : l'orbite caméra (qui utilise aussi ce bouton) doit être
  // désactivée le temps du geste (`controls.enabled`), sinon la vue tourne en même
  // temps qu'on monte/descend le décor.
  const SHIFT_DRAG_ELEVATE_PER_PX = 0.01;
  let lastShiftDragAt: { x: number; y: number } | null = null;

  const panAxis = new THREE.Vector3();
  /** Reprend la formule de pan interne d'`OrbitControls` (`_panLeft`), appliquée
   * uniquement à l'axe horizontal — voir le commentaire sur `mouseButtons.RIGHT`
   * plus haut. */
  function panCameraHorizontal(deltaX: number): void {
    const targetDistance = camera.position.distanceTo(controls.target);
    const distance = (2 * deltaX * targetDistance) / renderer.domElement.clientHeight;
    panAxis.setFromMatrixColumn(camera.matrix, 0);
    panAxis.multiplyScalar(-distance);
    camera.position.add(panAxis);
    controls.target.add(panAxis);
  }
  /** Dernière position connue pendant un glisser du clic droit — distincte de
   * `rightPointerDownAt` (position de départ, conservée pour mesurer la distance
   * totale parcourue et distinguer un clic court d'un glisser, voir
   * `handlePointerUp`) : celle-ci avance à chaque `pointermove` pour calculer un
   * delta pas à pas, comme `lastMiddleDragAt`. */
  let lastRightDragAt: { x: number; y: number } | null = null;

  function handlePointerMove(e: PointerEvent): void {
    if (lastMiddleDragAt) {
      const deltaDeg = (e.clientX - lastMiddleDragAt.x) * MIDDLE_DRAG_ROTATE_DEG_PER_PX;
      lastMiddleDragAt = { x: e.clientX, y: e.clientY };
      if (deltaDeg !== 0) options?.onMiddleDragRotate?.(deltaDeg);
      return;
    }
    if (lastShiftDragAt) {
      // Glisser vers le HAUT (delta Y écran négatif) doit FAIRE MONTER le décor
      // (élévation positive) — d'où le signe inversé.
      const deltaElevation = -(e.clientY - lastShiftDragAt.y) * SHIFT_DRAG_ELEVATE_PER_PX;
      lastShiftDragAt = { x: e.clientX, y: e.clientY };
      if (deltaElevation !== 0) options?.onShiftDragElevate?.(deltaElevation);
      return;
    }
    if (lastRightDragAt) {
      const deltaX = e.clientX - lastRightDragAt.x;
      lastRightDragAt = { x: e.clientX, y: e.clientY };
      if (deltaX !== 0) panCameraHorizontal(deltaX);
      return;
    }
    const point = pointerToGroundPoint(e);
    options?.onHoverPoint?.(point);
    setHoveredCell(gridCellFromPoint(point));
  }

  function handlePointerLeave(): void {
    lastMiddleDragAt = null;
    if (lastShiftDragAt) {
      lastShiftDragAt = null;
      controls.enabled = true;
    }
    options?.onHoverPoint?.(null);
    setHoveredCell(null);
  }

  // Un clic est distingué d'un glisser-orbiter par la distance parcourue entre
  // pointerdown et pointerup — sans ça, chaque rotation de caméra à la souris
  // déclencherait aussi un placement/retrait de pièce sur la case de départ.
  // Suivi séparément par bouton (gauche/droit) : OrbitControls utilise le bouton
  // droit pour le panoramique, un glisser-panoramique ne doit pas non plus
  // déclencher `onCellRightClick`.
  let pointerDownAt: { x: number; y: number } | null = null;
  let rightPointerDownAt: { x: number; y: number } | null = null;
  function resolveCellAtEvent(e: PointerEvent): { cell: GridCell; point: GroundPoint } | null {
    const point = pointerToGroundPoint(e);
    if (!point) return null;
    const gx = Math.floor(point.x);
    const gy = Math.floor(point.y);
    if (gx < 0 || gy < 0 || gx >= gridWidth || gy >= gridHeight) return null;
    return { cell: { x: gx, y: gy }, point };
  }
  function handlePointerDown(e: PointerEvent): void {
    if (e.button === 1) {
      // Sans ça, le navigateur déclenche son propre "défilement automatique"
      // (icône de molette, la page monte/descend au moindre mouvement de
      // souris) au clic molette — un geste standard hors contexte 3D, mais qui
      // entre directement en conflit avec la rotation de décor ci-dessous.
      e.preventDefault();
      lastMiddleDragAt = { x: e.clientX, y: e.clientY };
      return;
    }
    if (e.button === 2) {
      rightPointerDownAt = { x: e.clientX, y: e.clientY };
      lastRightDragAt = { x: e.clientX, y: e.clientY };
      return;
    }
    if (e.button !== 0) return;
    if (e.shiftKey) {
      // Réclame le geste entièrement pour l'élévation — sans ça, OrbitControls
      // (bouton gauche = orbite par défaut) tournerait la caméra en même temps.
      controls.enabled = false;
      lastShiftDragAt = { x: e.clientX, y: e.clientY };
      return;
    }
    pointerDownAt = { x: e.clientX, y: e.clientY };
  }
  function handlePointerUp(e: PointerEvent): void {
    if (e.button === 1) {
      lastMiddleDragAt = null;
      return;
    }
    if (lastShiftDragAt) {
      lastShiftDragAt = null;
      controls.enabled = true;
      return;
    }
    if (e.button === 2) {
      const down = rightPointerDownAt;
      rightPointerDownAt = null;
      lastRightDragAt = null;
      if (!down) return;
      const dragDist = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      if (dragDist > CLICK_DRAG_TOLERANCE_PX) return;
      const resolved = resolveCellAtEvent(e);
      if (!resolved) return;
      options?.onCellRightClick?.(resolved.cell, resolved.point);
      return;
    }
    if (e.button !== 0) return;
    const down = pointerDownAt;
    pointerDownAt = null;
    if (!down) return;
    const dragDist = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    if (dragDist > CLICK_DRAG_TOLERANCE_PX) return;
    const resolved = resolveCellAtEvent(e);
    if (!resolved) return;
    options?.onCellClick?.(resolved.cell, resolved.point);
  }
  function handleContextMenu(e: MouseEvent): void {
    e.preventDefault();
  }

  renderer.domElement.addEventListener('pointermove', handlePointerMove);
  renderer.domElement.addEventListener('pointerleave', handlePointerLeave);
  renderer.domElement.addEventListener('pointerdown', handlePointerDown);
  renderer.domElement.addEventListener('pointerup', handlePointerUp);
  renderer.domElement.addEventListener('contextmenu', handleContextMenu);

  let frameId = 0;
  function animate(): void {
    if (disposed) return;
    if (!topView) controls.update();
    renderer.render(scene, camera);
    frameId = requestAnimationFrame(animate);
  }
  frameId = requestAnimationFrame(animate);

  const resizeObserver = new ResizeObserver(() => {
    const w = container.clientWidth || width;
    const h = container.clientHeight || height;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  });
  resizeObserver.observe(container);

  return {
    dispose(): void {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      renderer.domElement.removeEventListener('pointerup', handlePointerUp);
      renderer.domElement.removeEventListener('contextmenu', handleContextMenu);
      controls.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          const material = obj.material;
          for (const m of Array.isArray(material) ? material : [material]) {
            if (m instanceof THREE.MeshStandardMaterial) m.map?.dispose();
            m.dispose();
          }
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    },
    setTopView(enabled: boolean): void {
      if (topView === enabled) return;
      topView = enabled;
      if (enabled) frameTopView();
      else framePerspective();
    },
    isTopView(): boolean {
      return topView;
    },
    recenter(): void {
      if (topView) frameTopView();
      else framePerspective();
    },
    refreshScene(nextCircuit: Circuit): void {
      latestCircuit = nextCircuit;
      gridWidth = nextCircuit.width;
      gridHeight = nextCircuit.height;
      disposeGroupChildren(roadGroup);
      const myRoadGeneration = ++roadGeneration;
      void loadTrackSceneRoad(
        roadGroup,
        buildAllTiles(nextCircuit),
        () => disposed || myRoadGeneration !== roadGeneration
      );
      disposeGroupChildren(decorGroup);
      const myDecorGeneration = ++decorGeneration;
      const isDecorDisposedNow = () => disposed || myDecorGeneration !== decorGeneration;
      void loadTrackSceneDecor(decorGroup, nextCircuit, isDecorDisposedNow);
      void loadFreeTrackSceneDecor(decorGroup, nextCircuit, isDecorDisposedNow);
      rebuildDebugMarkers(nextCircuit);
    },
    setDebugCornerMarkers(enabled: boolean): void {
      debugMarkersEnabled = enabled;
      rebuildDebugMarkers(latestCircuit);
    },
    setHighlightCells(cells: HighlightCell[]): void {
      applyHighlightCells(cells);
    },
    setGhostCorner(preview: GhostCornerPreview | null): void {
      applyGhostCorner(preview);
    },
    setGhostModel(preview: GhostModelPreview | null): void {
      applyGhostModel(preview);
    },
    setErrorCells(cells: GridCell[]): void {
      applyErrorCells(cells);
    },
    pauseForOverlay(): void {
      renderer.forceContextLoss();
    },
    resumeFromOverlay(): void {
      renderer.forceContextRestore();
    },
  };
}
